import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import { bootLocale } from './i18n';
import { router } from './router';
import { bootTheme } from './theme';
import './assets/main.css';

/**
 * Starts the app.
 *
 * **A function, and not top-level `await`, and this is not a style preference.** Everything the
 * router loads is a lazy chunk, and every one of those chunks imports this one — that is what
 * code splitting does with the shared modules. A module that is still evaluating cannot satisfy
 * an import, so an `await` out here for anything that ends in a dynamic import waits for a chunk
 * that is waiting for this module: a deadlock, and it shows as a permanently blank page rather
 * than as an error. Inside a function the module finishes evaluating immediately and the chunks
 * can load.
 *
 * `bootLocale` is safe today only because the message chunks import nothing. It would stop being
 * safe the day one of them needed something from here.
 */
async function boot(): Promise<void> {
  /*
   * A remembered appearance, applied before anything is painted. Synchronous and first: it only
   * writes an attribute, and the stylesheet is already painting the reader's system preference
   * without it — so this is the narrow case of somebody who chose *against* their system, and the
   * flash it avoids is the one they would notice most.
   */
  bootTheme();

  /*
   * The language is resolved *before* mount, deliberately. The route guard's /me call lands a
   * moment after the first paint, so a visit that painted in English and then flipped is what
   * this await buys — see docs/ROADMAP.md, Phase 9.
   */
  await bootLocale();

  const app = createApp(App).use(createPinia()).use(router);

  /*
   * Mounted once the first view has resolved, so the shell and its content arrive in one frame.
   *
   * Without this the shell painted on its own — header, empty `<main>`, footer — and the footer
   * fell the height of the page when the route's chunk landed a round trip later. That is the
   * larger half of the CLS of 1.0 Cloudflare's RUM measured on the landing page, and arranging
   * the shell differently cannot fix it: an element that is on screen and then moves has
   * shifted, and the only way it cannot is not to be on screen first.
   *
   * It costs a moment of blank page, which is the trade worth making. A bare header and footer
   * are not content — there is nothing in them to read or tap — so what they buy is the
   * *appearance* of progress, at the price of the page rearranging itself under a thumb.
   *
   * `isReady` waits for the route's component and, on a guarded route only, for /me. The guard
   * does not wait for /me anywhere else, which is what keeps this to the chunk. See src/router.ts.
   */
  await router.isReady();

  app.mount('#app');
}

void boot();
